import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Users, TrendingUp } from "lucide-react";

export function UserLimitModal({ isOpen, onClose, limitDetails }) {
  const {
    current_users = 0,
    max_users = 0,
    plan = 'base',
    extra_user_price = 4
  } = limitDetails || {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">User Limit Reached</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-orange-800">
                <p className="font-medium mb-1">You've reached your user limit</p>
                <p className="text-orange-700">
                  Your current plan includes <strong>{max_users} users</strong>, 
                  and you currently have <strong>{current_users} active users</strong>.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-600" />
                Current Status
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Active Users:</span>
                  <span className="font-medium text-gray-900">{current_users} / {max_users}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium text-gray-900 capitalize">{plan}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Options to Continue
              </h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>
                    <strong>Add Extra Users:</strong> €{extra_user_price}/user/month
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>
                    <strong>Upgrade Plan:</strong> Get more users and features
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 sm:flex-initial"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="flex-1 sm:flex-initial bg-blue-600 hover:bg-blue-700"
            onClick={() => {
              // TODO: Navigate to pricing/upgrade page
              console.log('Navigate to upgrade page');
              onClose();
            }}
          >
            View Plans & Pricing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
